import { Box, Typography, makeStyles } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import clsx from "clsx";
import * as React from "react";

import theme from "@/theme";

import { useCurrentStepMotionStyles } from "./useCurrentStepMotionStyles";

// 診断フローのステップ表示チップ（1ステップ分の atom）。
// 見た目は Figma（UI_design: 9911-4478）の3状態に対応する。
// - completed: グレー塗り＋白文字
// - active:    プライマリ塗り＋白文字
// - upcoming:  グレー枠線＋グレー文字（未到達）
// 文言・✓の有無は呼び出し元が明示的に渡す（このコンポーネントは利用箇所を知らない）。
export type DiagnosisStepChipState = "completed" | "active" | "upcoming";

const useStyles = makeStyles(() => ({
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    borderRadius: 20,
    padding: theme.spacing(0.5, 1.25),
  },
  label: {
    fontSize: 12,
  },
  checkIcon: {
    fontSize: 16,
  },
  completed: {
    backgroundColor: theme.palette.black[300],
    color: theme.palette.common.white,
    "& $label": {
      fontWeight: 500,
    },
  },
  active: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "& $label": {
      fontWeight: 700,
    },
  },
  upcoming: {
    // border だとチップの外形が塗りの状態より 2px 大きくなるため inset の boxShadow で枠線を引く
    boxShadow: `inset 0 0 0 1px ${theme.palette.text.disabled}`,
    color: theme.palette.text.disabled,
    "& $label": {
      fontWeight: 500,
    },
  },
  v2: {
    borderRadius: 9999,
    padding: "8px 12px",
    color: theme.palette.text.secondary,
    "& $label": { fontWeight: 500, lineHeight: 1.3 },
    "& $checkIcon": { fontSize: 14 },
    "&$completed": { backgroundColor: theme.palette.semantic["state/selected-bg"], color: theme.palette.primary.main },
    "&$active": { color: theme.palette.common.white },
    "&$upcoming": {
      backgroundColor: theme.palette.common.white,
      boxShadow: `inset 0 0 0 1px ${theme.palette.semantic["border/strong"]}`,
    },
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
}));

interface DiagnosisStepChipProps {
  label: string;
  state: DiagnosisStepChipState;
  withCheck: boolean;
  v2?: boolean;
  // 登録・診断のフェーズ表示で有効化する。別フローのチップは静止のまま。
  animate?: boolean;
}

export const DiagnosisStepChip: React.FC<DiagnosisStepChipProps> = ({
  label,
  state,
  withCheck,
  v2 = false,
  animate = false,
}) => {
  const classes = useStyles();
  const motionClasses = useCurrentStepMotionStyles();

  return (
    <Box
      aria-current={state === "active" ? "step" : undefined}
      className={clsx(
        classes.chip,
        classes[state],
        v2 && classes.v2,
        animate && state === "active" && !withCheck && motionClasses.current
      )}
    >
      {withCheck &&
        (v2 && state === "completed" ? <img src="/icons/ds-v2-step-check.svg" alt="" /> : <CheckIcon className={classes.checkIcon} />)}
      <Typography component="span" className={classes.label} style={{ color: "inherit" }}>
        {label}
      </Typography>
    </Box>
  );
};

// 3チップ横並びの共通レイアウト（各フロー用 *StepChips から利用する）
export const DiagnosisStepChipsRow: React.FC = ({ children }) => {
  const classes = useStyles();

  return <Box className={classes.row}>{children}</Box>;
};
